import React, { useState, useEffect, useRef } from 'react';
import './JoystickControl.css';

// Joystick toggle icon component that changes based on visibility state
const JoystickIcon = ({ isVisible }) => (
  <svg 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Base circle */}
    <circle cx="12" cy="12" r="9" stroke="white" strokeWidth="1.5" />
    
    {/* Center dot */}
    <circle cx="12" cy="12" r="2" fill="white" />
    
    {/* Show/hide indicator */}
    {isVisible ? (
      // X mark for hide
      <>
        <line x1="6" y1="6" x2="18" y2="18" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="18" y1="6" x2="6" y2="18" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      </>
    ) : (
      // Plus mark for show
      <>
        <line x1="12" y1="7" x2="12" y2="17" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="7" y1="12" x2="17" y2="12" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      </>
    )}
  </svg>
);

const JoystickControl = ({ onChange, size = 200 }) => {
  const joystickRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [showJoystick, setShowJoystick] = useState(false);
  
  // Calculate the maximum distance the joystick can move (radius)
  const maxDistance = size / 2 - 20; // Reduced handle offset from 30 to 20 to allow larger movement radius
  
  // Calculate distance and angle from center
  const distance = Math.sqrt(position.x * position.x + position.y * position.y);
  const normalizedDistance = Math.min(distance, maxDistance) / maxDistance;
  const angle = Math.atan2(position.y, position.x) * (180 / Math.PI);
  
  // Calculate arrow position
  const arrowLength = normalizedDistance * maxDistance;
  const arrowX = arrowLength * Math.cos(angle * (Math.PI / 180));
  const arrowY = arrowLength * Math.sin(angle * (Math.PI / 180));
  
  // Handle mouse/touch down
  const handleStart = (clientX, clientY) => {
    if (!joystickRef.current || !showJoystick) return;
    
    const rect = joystickRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    updatePosition(clientX - centerX, clientY - centerY);
    setIsDragging(true);
  };
  
  // Handle mouse/touch move
  const handleMove = (clientX, clientY) => {
    if (!isDragging || !joystickRef.current || !showJoystick) return;
    
    const rect = joystickRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    updatePosition(clientX - centerX, clientY - centerY);
  };
  
  // Handle mouse/touch up/end
  const handleEnd = () => {
    if (!showJoystick) return;
    
    setIsDragging(false);
    // Reset to center when released
    setPosition({ x: 0, y: 0 });
    
    if (onChange) {
      onChange({ x: 0, y: 0, angle: 0, distance: 0 });
    }
  };
  
  // Helper to update position with constraints
  const updatePosition = (x, y) => {
    if (!showJoystick) return;
    
    // Calculate distance from center
    const dist = Math.sqrt(x * x + y * y);
    
    // If distance is greater than max, scale down x and y
    if (dist > maxDistance) {
      const scale = maxDistance / dist;
      x *= scale;
      y *= scale;
    }
    
    setPosition({ x, y });
    
    // Calculate normalized values (-1 to 1)
    const normalizedX = x / maxDistance;
    const normalizedY = y / maxDistance;
    const newDistance = Math.sqrt(normalizedX * normalizedX + normalizedY * normalizedY);
    const newAngle = Math.atan2(y, x) * (180 / Math.PI);
    
    if (onChange && showJoystick) {
      onChange({ 
        x: normalizedX, 
        y: normalizedY, 
        angle: newAngle,
        distance: newDistance
      });
    }
  };
  
  // Mouse event handlers
  const onMouseDown = (e) => {
    handleStart(e.clientX, e.clientY);
  };
  
  const onMouseMove = (e) => {
    handleMove(e.clientX, e.clientY);
  };
  
  const onMouseUp = () => {
    handleEnd();
  };
  
  // Touch event handlers
  const onTouchStart = (e) => {
    const touch = e.touches[0];
    handleStart(touch.clientX, touch.clientY);
  };
  
  const onTouchMove = (e) => {
    const touch = e.touches[0];
    handleMove(touch.clientX, touch.clientY);
  };
  
  const onTouchEnd = () => {
    handleEnd();
  };
  
  // Add mouse/touch event listeners
  useEffect(() => {
    if (!showJoystick) return;
    
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
    
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [isDragging, showJoystick]);
  
  // Toggle joystick visibility
  const toggleJoystick = () => {
    console.log("Toggle button clicked, current state:", showJoystick);
    setShowJoystick(prevState => !prevState);
  };
  
  return (
    <div className={`joystick-container ${showJoystick ? 'visible' : ''}`}>
      <button 
        className="joystick-toggle" 
        onClick={toggleJoystick}
        type="button"
        aria-label={showJoystick ? "Hide joystick control" : "Show joystick control"}
      >
        <JoystickIcon isVisible={showJoystick} />
      </button>
      
      {showJoystick && (
        <div 
          className="joystick" 
          ref={joystickRef}
          style={{ width: size, height: size }}
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
        >
          <div className="joystick-base">
            {/* Crosshair */}
            <div className="joystick-crosshair-h"></div>
            <div className="joystick-crosshair-v"></div>
            
            {/* Direction arrow */}
            {normalizedDistance > 0.05 && (
              <div className="joystick-arrow" style={{
                width: `${arrowLength}px`,
                transform: `rotate(${angle}deg)`
              }}></div>
            )}
            
            {/* Handle */}
            <div 
              className="joystick-handle" 
              style={{
                transform: `translate(${position.x}px, ${position.y}px)`
              }}
            ></div>
          </div>
          
          {/* Directional info */}
          <div className="joystick-info">
            <div>Angle: {Math.round(angle)}°</div>
            <div>Distance: {Math.round(normalizedDistance * 100)}%</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JoystickControl; 